import { ItemGenerator } from './ItemGenerator.js';
import * as CarTierSystem from './CarTierSystem.js';
import * as PrestigeSystem from './PrestigeSystem.js';
import { SaveManager } from './SaveManager.js';
import { WaveStateManager } from './WaveStateManager.js';
import { PrestigeStateManager } from '../state/PrestigeStateManager.js';
import { CollectionStateManager } from '../state/CollectionStateManager.js';
import { UIState } from '../state/UIState.js';
import { DriverProgressManager } from '../state/DriverProgressManager.js';
import { upgradeRegistry } from '../registries/UpgradeRegistry.js';
import { itemRegistry } from '../registries/ItemRegistry.js';
import { UpgradeSystem } from '../systems/UpgradeSystem.js';
import { InventorySystem } from '../systems/InventorySystem.js';
import { getWaveSystem } from '../systems/WaveSystem.js';
import { getPrestigeSystemWrapper } from '../systems/PrestigeSystemWrapper.js';
import { getPackOpenerSystem } from '../systems/PackOpenerSystem.js';
import { eventBus, GameEvents } from '../utils/EventBus.js';

/**
 * GameState - Central game state container
 *
 * State is organized into slices managed by dedicated managers:
 * - this.waveState (WaveStateManager) - Wave/pack operations
 * - this.uiState (UIState) - UI tracking (seen items, tutorials)
 * - this.prestigeState (PrestigeStateManager) - Prestige/ascension
 * - this.driverProgress (DriverProgressManager) - Driver skills
 *
 * Core state (miles, fuel, inventory, cars) remains in GameState.
 * Save/load operates on this.state which contains all slices.
 *
 * Section Organization:
 * - CORE STATE: miles, fuel, gameTime, save/load
 * - DRIVER SKILLS: delegated to DriverProgressManager (deprecated methods remain)
 * - FUEL: consumable resource for offline driving
 * - WAVEDUST CURRENCY: item quality fusion currency
 * - OFFLINE: session state, offline progress calculation
 * - CAR: tier system, progression, purchases
 * - LEADERBOARD: period tracking (weekly/daily miles)
 * - UPGRADES: car upgrade purchase methods
 * - WAVES: delegated to WaveStateManager
 * - INVENTORY: item management
 * - QUALITY FUSION: item upgrade system
 * - PRESTIGE: player prestige (car reset with bonuses)
 * - AUDIO: audio settings
 */

// Item upgrade costs by rarity (9 levels of upgrades: 1→2, 2→3, ... 9→10)
const ITEM_UPGRADE_COSTS = {
    faded: [10, 20, 30, 40, 50, 60, 70, 80, 90],
    standard: [15, 30, 45, 60, 75, 90, 105, 120, 135],
    pristine: [25, 50, 75, 100, 125, 150, 175, 200, 225],
    golden: [40, 80, 120, 160, 200, 240, 280, 320, 360],
    vectorized: [60, 120, 180, 240, 300, 360, 420, 480, 540]
};

const MAX_ITEM_LEVEL = 10;

// Matches main game's perceptual scaling for offline → active parity
// DriveStateMachine uses (40/60)/4.545 for visual speed, combined with gameUnitsPerMile=450
const MAIN_GAME_MILES_FACTOR = (40/60) / 4.545 * 3600 / 450;  // ≈ 1.173

// Game state management and persistence
export class GameState {
    constructor(upgradesConfig, carsConfig) {
        // Upgrade definitions (loaded from JSON)
        this.upgrades = upgradesConfig;

        // Car definitions (loaded from JSON)
        this.carsConfig = carsConfig;

        // Car progression order
        this.carOrder = ['starter', 'sedan', 'sports', 'supercar', 'hypercar'];

        // Conversion constants
        // With world scale correction (/4.545), 528 units = 1 mile
        // At 30 MPH: 4.4 units/sec * 3600 = 15,840 units/hour = 30 miles
        this.gameUnitsPerMile = 450;
        this.distanceAtLastMileUpdate = 0;

        // Initialize save manager for persistence
        this.saveManager = new SaveManager(carsConfig, upgradesConfig);

        // Unified TimeScale System
        // Controls ALL speed: visual movement, mile accumulation, and day/night cycle
        // At timeScale=1: real time (60 MPH = 60 miles/hour, 24h day = 24 real hours)
        // At timeScale>1: time compressed (everything speeds up proportionally)
        // Item bonuses multiply this base value
        this.BASE_TIME_SCALE = 3; // Real time (items/upgrades increase this)
        this.GAME_DAY_LENGTH = 86400; // 24 hours in game seconds

        // Reference to stat calculator (set by main.js after initialization)
        // Single source of truth for all stat calculations with caching
        this.statCalculator = null;

        // Deferred initialization flag
        // State is NOT loaded in constructor - must call initializeState() after
        // the correct save slot has been determined (after auth resolves)
        this._stateInitialized = false;

        // Create placeholder state (will be replaced by initializeState)
        // This prevents null reference errors during early initialization
        this.state = this.createInitialState();

        // Wave state manager (initialized in initializeState after real state loads)
        this.waveState = null;

        // Prestige state manager (initialized in initializeState after real state loads)
        this.prestigeState = null;

        // Initialize UpgradeRegistry from the loaded upgrades config
        // This populates the registry with all upgrade definitions for registry-based access
        this._initializeUpgradeRegistry(upgradesConfig);
    }

    /**
     * Initialize UpgradeRegistry from legacy upgrades config format.
     * Converts the key-value config to registry format and registers all upgrades.
     * @param {Object} upgradesConfig - Legacy upgrades config from public/data/upgrades.json
     * @private
     */
    _initializeUpgradeRegistry(upgradesConfig) {
        // Clear existing entries (in case of re-initialization)
        upgradeRegistry.clear();

        // Map upgrade types to categories for the registry
        const categoryMap = {
            baseSpeed: 'speed',
            acceleration: 'speed',
            maxSpeed: 'speed',
            cruiseDuration: 'utility',
            cruiseCooldown: 'utility',
            momentum: 'momentum',
            momentumRate: 'momentum'
        };

        // Convert legacy format to registry format and register each upgrade
        const upgrades = [];
        for (const [id, config] of Object.entries(upgradesConfig)) {
            const upgrade = {
                id,
                name: config.name,
                category: categoryMap[id] || 'special',
                description: config.description,
                baseCost: config.baseCosts?.[0] || 5,
                baseCosts: config.baseCosts,
                maxLevel: config.maxLevel,
                effect: {
                    type: id,
                    description: config.description
                }
            };
            upgrades.push(upgrade);
        }

        // Load into registry using JSON format
        upgradeRegistry.loadFromJSON({
            upgrades,
            categories: ['speed', 'utility', 'momentum', 'special']
        });
    }

    /**
     * Get the UpgradeRegistry instance.
     * Provides access to upgrade definitions through the registry API.
     * @returns {UpgradeRegistry} The upgrade registry singleton
     */
    getUpgradeRegistry() {
        return upgradeRegistry;
    }

    /**
     * Get the UIState instance.
     * Provides access to UI state tracking (seen items/waves, tutorial state).
     * @returns {UIState} The UI state manager
     */
    getUIState() {
        return this.uiState;
    }

    /**
     * Get the UpgradeSystem instance.
     * Provides access to upgrade purchases with EventBus integration.
     * @returns {UpgradeSystem}
     */
    getUpgradeSystem() {
        return this.upgradeSystem;
    }

    /**
     * Get the InventorySystem instance.
     * Provides access to inventory operations with EventBus integration.
     * @returns {InventorySystem}
     */
    getInventorySystem() {
        return this.inventorySystem;
    }

    /**
     * Get the WaveSystem instance.
     * Provides access to wave/pack operations with EventBus integration.
     * @returns {WaveSystem}
     */
    getWaveSystem() {
        return getWaveSystem(this);
    }

    /**
     * Get the PrestigeSystemWrapper instance.
     * Provides access to prestige operations with EventBus integration.
     * @returns {PrestigeSystemWrapper}
     */
    getPrestigeSystemWrapper() {
        return getPrestigeSystemWrapper(this);
    }

    /**
     * Get the PackOpenerSystem instance.
     * Returns existing instance (created in main.js with packGenerator).
     * @returns {PackOpenerSystem}
     */
    getPackOpenerSystem() {
        // Returns existing instance (created in main.js with packGenerator)
        return getPackOpenerSystem();
    }

    /**
     * Initialize state after the correct save slot has been determined.
     * This must be called after AccountManager.waitForReady() resolves
     * and SaveManager has been switched to the correct slot.
     *
     * @returns {boolean} True if this was first initialization, false if already initialized
     */
    initializeState() {
        if (this._stateInitialized) {
            console.log('[GameState] Already initialized, skipping');
            return false;
        }

        console.log('[GameState] Initializing state for slot:', this.saveManager.getActiveSaveKey());

        // Load or create initial state from the now-correct slot
        const loadedState = this.saveManager.load();
        const isNewGame = !loadedState;
        this.state = loadedState || this.createInitialState();

        // Initialize game time if not loaded from save
        if (this.state.gameTime === undefined) {
            this.state.gameTime = 43200; // Default to noon (12:00 PM in game seconds)
        }

        // Initialize wave state manager
        this.waveState = new WaveStateManager(this);

        // Initialize prestige state manager
        this.prestigeState = new PrestigeStateManager(this);

        // Initialize collection state manager (persists forever across prestiges)
        this.collectionStateManager = new CollectionStateManager(this);

        // Initialize driver progress manager
        this.driverProgress = new DriverProgressManager(this);

        // Initialize UI state manager
        this.uiState = new UIState(this);

        // Initialize systems (after state is ready)
        this.upgradeSystem = new UpgradeSystem(this);
        this.inventorySystem = new InventorySystem(this);

        // Initialize Phase 5 systems (after state managers are ready)
        getWaveSystem(this);
        getPrestigeSystemWrapper(this);
        // PackOpenerSystem needs packGenerator - initialized later in main.js

        // Log state manager initialization status
        console.log('[GameState] State managers initialized:', {
            waveState: !!this.waveState,
            uiState: !!this.uiState,
            prestigeState: !!this.prestigeState,
            collectionStateManager: !!this.collectionStateManager,
            driverProgress: !!this.driverProgress
        });
        console.log('[GameState] Systems initialized:', {
            upgradeSystem: !!this.upgradeSystem,
            inventorySystem: !!this.inventorySystem,
            waveSystem: !!getWaveSystem(this),
            prestigeSystemWrapper: !!getPrestigeSystemWrapper(this)
        });

        // Force save immediately after load to persist any migrations
        if (!isNewGame) {
            this.saveManager.save(this.state, true);
        }

        this._stateInitialized = true;
        console.log('[GameState] State initialized:', {
            miles: this.state.totalMiles,
            fuel: this.state.fuel,
            isNewGame
        });

        return true;
    }

    /**
     * Check if state has been initialized
     * @returns {boolean}
     */
    isInitialized() {
        return this._stateInitialized;
    }

    // Static method to load configs from JSON files
    static async loadConfigs() {
        try {
            const [upgradesResponse, carsResponse] = await Promise.all([
                fetch('data/upgrades.json'),
                fetch('data/cars.json')
            ]);

            const upgrades = await upgradesResponse.json();
            const cars = await carsResponse.json();

            return { upgrades, cars };
        } catch (error) {
            console.error('Error loading game configs:', error);
            throw error;
        }
    }

    createInitialState() {
        return {
            miles: 0,
            totalMiles: 0, // Miles this prestige cycle (resets on prestige)
            lifetimeMiles: 0, // True lifetime miles (NEVER resets, used for leaderboards)
            gameTime: 43200, // Default to noon (12:00 PM in game seconds)
            currentCarId: 'starter',
            cars: JSON.parse(JSON.stringify(this.carsConfig)), // Deep copy from config
            inventory: {
                items: [], // Array of item IDs collected
                equippedSlots: {
                    dashboard: null,   // Item ID or null
                    keychain: null,
                    rearview: null
                },
                seenItemIds: [] // Item IDs user has seen in dash tab
            },
            waves: {
                nextMilestone: 25,      // Legacy - kept in sync with nextWaveAtMile
                nextWaveAtMile: 25,     // Dynamic spawn target (replaces fixed milestones)
                nextWaveQuality: 0.1,   // Quality of the next wave (0.0-1.0)
                luckAccumulator: 0,     // Luck system for drought/bounty (-1 to 1)
                lastSpawnDistance: 100, // Distance of last spawn (for luck calculation)
                futureWaves: [],        // Pre-rolled upcoming waves: [{ atMile, quality }]
                availablePacks: [],     // Array of { milestone, cost, packQuality }
                seenWaveMilestones: [], // Milestones user has seen in waves tab
                packsPurchased: 0,
                tutorialShown: false
            },
            prestige: {
                level: 0,
                mileEarningBonus: 0,   // Total % bonus (always granted)
                // Skill bonuses - player chooses which skill to boost each prestige
                skillBonuses: {
                    baseSpeed: 0,
                    acceleration: 0,
                    maxSpeed: 0,
                    cruiseDuration: 0,
                    cruiseCooldown: 0,
                    momentum: 0,
                    momentumRate: 0
                }
            },
            audioSettings: {
                sfxVolume: 100,    // 0-100
                musicVolume: 50    // 0-100
            },
            // Session state for offline simulation
            session: {
                exitTime: null,         // Real timestamp when game was last exited
                exitMomentum: 0,        // Momentum value at exit
                exitTotalMiles: 0,      // Total miles at exit (for wave calculations)
                exitGameTime: 0         // Game time of day at exit
            },
            // Driver skills (global upgrades that apply across all cars)
            driver: {
                fuelTank: { level: 0 },         // Max fuel capacity (offline simulation time)
                scrapBonus: { level: 0 },       // Bonus miles from scrapping
                itemUpgradeBonus: { level: 0 }, // Item upgrade effect scaling
                waveLuck: { level: 0 }          // Wave quality bonus
            },
            // Fuel resource (seconds of offline driving available)
            fuel: 30 * 60,  // Start with 30 minutes (base tank capacity)
            // Wavedust currency (for item quality fusion)
            wavedust: 0,
            // Leaderboard period tracking (for weekly/daily miles calculation)
            leaderboardPeriods: {
                weekStartTime: null,      // UTC timestamp of Monday 00:00
                weekStartMiles: 0,        // totalMiles at week start
                dayStartTime: null,       // UTC timestamp of day start (00:00 UTC)
                dayStartMiles: 0          // totalMiles at day start
            },
            // Collection tracking (persists forever, never wiped on prestige)
            collection: {
                found: [],              // Array of baseId strings found
                mastery: {},            // { baseId: timesFound } for mastery tracking
                claimedMilestones: []   // Array of threshold values already claimed
            }
        };
    }

    // =========================================================================
    // DRIVER SKILL COST ARRAY (shared by all driver skills)
    // =========================================================================
    /**
     * @deprecated Use DriverProgressManager.DRIVER_SKILL_COSTS instead
     */
    static DRIVER_SKILL_COSTS = [25, 50, 100, 175, 300, 500, 850, 1400, 2300, 3800];

    /**
     * Get the DriverProgressManager instance.
     * @returns {DriverProgressManager} The driver progress manager
     */
    getDriverProgressManager() {
        return this.driverProgress;
    }

    // =========================================================================
    // FUEL TANK - Maximum fuel capacity for offline driving
    // =========================================================================

    /**
     * Get fuel tank capacity in seconds based on Fuel Tank level + item bonuses + collection bonuses
     * Level 0: 30 minutes, Level 10: 12 hours, plus any item fuelTank effects and collection milestone bonuses
     */
    getFuelTankCapacity() {
        // Base capacity from driver skill
        let capacity = this.driverProgress ? this.driverProgress.getFuelTankCapacity() : 30 * 60;

        // Add item effects (fuelTank items provide additive bonus in seconds)
        if (this.statCalculator) {
            const itemEffects = this.statCalculator.calculateActiveItemEffects().fuelTank;
            if (itemEffects) {
                capacity += itemEffects.add;
            }
            // Add collection milestone bonus (fuelTank bonus is in seconds)
            capacity += this.statCalculator.getCollectionBonus('fuelTank');
        }

        return capacity;
    }

    /**
     * Get fuel tank upgrade cost
     * @deprecated Use driverProgress.getFuelTankCost() instead
     */
    getFuelTankCost() {
        if (!this.driverProgress) return 25; // Default: first level cost
        return this.driverProgress.getFuelTankCost();
    }

    /**
     * Upgrade fuel tank capacity
     * @deprecated Use driverProgress.upgradeFuelTank() instead
     */
    upgradeFuelTank() {
        if (!this.driverProgress) return false;
        return this.driverProgress.upgradeFuelTank();
    }

    // =========================================================================
    // FUEL - Consumable resource for offline driving
    // =========================================================================

    /**
     * Get current fuel amount in seconds
     */
    getFuel() {
        return this.state.fuel ?? 0;
    }

    /**
     * Calculate fuel cost for a given amount of fuel (in seconds)
     * Cost scales progressively - larger fills cost more per hour
     * @param {number} fuelSeconds - Amount of fuel in seconds
     * @returns {number} Cost in miles
     */
    calculateFuelCost(fuelSeconds) {
        if (fuelSeconds <= 0) return 0;

        const fuelHours = fuelSeconds / 3600;
        const baseRate = 25; // 25 mi per hour base
        const scalingFactor = 1 + (fuelHours / 18); // Cost per hour increases with fill amount
        const cost = Math.ceil(fuelHours * baseRate * scalingFactor);
        return Math.max(1, cost); // Minimum 1 mile
    }

    /**
     * Get cost to refuel (fill tank to capacity)
     * Cost scales with amount of fuel needed
     */
    getRefuelCost() {
        const capacity = this.getFuelTankCapacity();
        const currentFuel = this.getFuel();
        const fuelNeeded = capacity - currentFuel;

        if (fuelNeeded <= 0) return null; // Tank is full

        return this.calculateFuelCost(fuelNeeded);
    }

    /**
     * Get cost to add a specific amount of fuel
     * @param {number} targetFuel - Target fuel level in seconds
     * @returns {number|null} Cost in miles, or null if invalid
     */
    getPartialRefuelCost(targetFuel) {
        const capacity = this.getFuelTankCapacity();
        const currentFuel = this.getFuel();

        // Clamp target to valid range
        const clampedTarget = Math.min(targetFuel, capacity);
        const fuelToAdd = clampedTarget - currentFuel;

        if (fuelToAdd <= 0) return null; // No fuel to add

        return this.calculateFuelCost(fuelToAdd);
    }

    /**
     * Check if player can refuel (tank not full and can afford)
     */
    canRefuel() {
        const cost = this.getRefuelCost();
        return cost !== null && this.state.miles >= cost;
    }

    /**
     * Check if player can afford a partial refuel
     * @param {number} targetFuel - Target fuel level in seconds
     */
    canPartialRefuel(targetFuel) {
        const cost = this.getPartialRefuelCost(targetFuel);
        return cost !== null && this.state.miles >= cost;
    }

    /**
     * Purchase fuel to fill tank to capacity
     */
    refuel() {
        const cost = this.getRefuelCost();
        if (cost === null || this.state.miles < cost) return false;

        this.state.miles -= cost;
        console.log(`[GameState] Spent ${cost} miles on Refuel`);
        this.state.fuel = this.getFuelTankCapacity();
        this.save();
        return true;
    }

    /**
     * Purchase a specific amount of fuel
     * @param {number} targetFuel - Target fuel level in seconds
     * @returns {boolean} Success
     */
    partialRefuel(targetFuel) {
        const cost = this.getPartialRefuelCost(targetFuel);
        if (cost === null || this.state.miles < cost) return false;

        const capacity = this.getFuelTankCapacity();
        this.state.miles -= cost;
        console.log(`[GameState] Spent ${cost} miles on Partial Refuel`);
        this.state.fuel = Math.min(targetFuel, capacity);
        this.save();
        return true;
    }

    /**
     * Get the maximum fuel level the player can afford
     * Uses binary search to find the highest fuel level within budget
     * @returns {number} Max affordable fuel level in seconds
     */
    getMaxAffordableFuel() {
        const capacity = this.getFuelTankCapacity();
        const currentFuel = this.getFuel();
        const playerMiles = this.state.miles;

        let low = currentFuel;
        let high = capacity;
        let maxAffordable = currentFuel;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const cost = this.getPartialRefuelCost(mid);

            if (cost !== null && cost <= playerMiles) {
                maxAffordable = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        return maxAffordable;
    }

    /**
     * Consume fuel (used by offline simulator)
     * @param {number} seconds - Amount of fuel to consume
     */
    consumeFuel(seconds) {
        this.state.fuel = Math.max(0, (this.state.fuel ?? 0) - seconds);
        this.save();
    }

    /**
     * Set fuel directly (used for initialization/migration)
     */
    setFuel(seconds) {
        const capacity = this.getFuelTankCapacity();
        this.state.fuel = Math.min(seconds, capacity);
    }

    // =========================================================================
    // WAVEDUST CURRENCY - Used for item quality fusion
    // =========================================================================

    /**
     * Get current wavedust amount
     * @returns {number} Current wavedust
     */
    getWavedust() {
        return this.state.wavedust || 0;
    }

    /**
     * Add wavedust
     * @param {number} amount - Amount to add
     */
    addWavedust(amount) {
        if (amount <= 0) return;
        this.state.wavedust = (this.state.wavedust || 0) + amount;
        this.save();
    }

    /**
     * Spend wavedust
     * @param {number} amount - Amount to spend
     * @returns {boolean} True if successful
     */
    spendWavedust(amount) {
        if (!this.canAffordWavedust(amount)) return false;
        this.state.wavedust -= amount;
        this.save();
        return true;
    }

    /**
     * Check if player can afford wavedust cost
     * @param {number} amount - Amount to check
     * @returns {boolean} True if affordable
     */
    canAffordWavedust(amount) {
        return this.getWavedust() >= amount;
    }

    // =========================================================================
    // SCRAP BONUS - Increases wavedust from scrapping waves/items
    // =========================================================================

    /**
     * Get scrap bonus upgrade cost
     * @deprecated Use driverProgress.getScrapBonusCost() instead
     */
    getScrapBonusCost() {
        if (!this.driverProgress) return 25; // Default: first level cost
        return this.driverProgress.getScrapBonusCost();
    }

    /**
     * Upgrade scrap bonus
     * @deprecated Use driverProgress.upgradeScrapBonus() instead
     */
    upgradeScrapBonus() {
        if (!this.driverProgress) return false;
        return this.driverProgress.upgradeScrapBonus();
    }

    /**
     * Get scrap bonus multiplier (1.0 to 1.5+ with items and collection bonuses)
     * Includes driver skill bonus, item salvageBonus effects, and collection milestone bonuses.
     * @param {boolean} noisy - If true, applies +/-10% random variance to skill bonus
     */
    getScrapBonusMultiplier(noisy = false) {
        // Base multiplier from driver skill (1.0 to 1.5)
        let multiplier = this.driverProgress ? this.driverProgress.getScrapBonusMultiplier(noisy) : 1.0;

        // Add item effects (salvageBonus items provide additive bonus)
        if (this.statCalculator) {
            const itemEffects = this.statCalculator.calculateActiveItemEffects().salvageBonus;
            if (itemEffects) {
                multiplier += itemEffects.add;
            }
            // Add collection milestone bonus (salvageBonus is additive percentage)
            multiplier += this.statCalculator.getCollectionBonus('salvageBonus');
        }

        return multiplier;
    }

    // =========================================================================
    // ITEM UPGRADE BONUS - Increases item upgrade effect scaling
    // =========================================================================

    /**
     * Get item upgrade bonus upgrade cost
     * @deprecated Use driverProgress.getItemUpgradeBonusCost() instead
     */
    getItemUpgradeBonusCost() {
        if (!this.driverProgress) return 25; // Default: first level cost
        return this.driverProgress.getItemUpgradeBonusCost();
    }

    /**
     * Upgrade item upgrade bonus
     * @deprecated Use driverProgress.upgradeItemUpgradeBonus() instead
     */
    upgradeItemUpgradeBonus() {
        if (!this.driverProgress) return false;
        return this.driverProgress.upgradeItemUpgradeBonus();
    }

    /**
     * Get item upgrade bonus multiplier (1.0 to 1.25+ with items and collection bonuses)
     * This multiplies the per-level bonus items get when upgraded.
     * Includes driver skill bonus, item effects, and collection milestone bonuses.
     */
    getItemUpgradeBonusMultiplier() {
        // Base multiplier from driver skill (1.0 to 1.25)
        let multiplier = this.driverProgress ? this.driverProgress.getItemUpgradeBonusMultiplier() : 1.0;

        // Add item effects (itemUpgradeBonus items provide additive percentage)
        if (this.statCalculator) {
            const itemEffects = this.statCalculator.calculateActiveItemEffects().itemUpgradeBonus;
            if (itemEffects) {
                multiplier += itemEffects.add;
            }
            // Add collection milestone bonus (itemUpgradeBonus is additive percentage)
            multiplier += this.statCalculator.getCollectionBonus('itemUpgradeBonus');
        }

        return multiplier;
    }

    // =========================================================================
    // WAVE LUCK - Increases chance of better tier waves
    // =========================================================================

    /**
     * Get wave luck upgrade cost
     * @deprecated Use driverProgress.getWaveLuckCost() instead
     */
    getWaveLuckCost() {
        if (!this.driverProgress) return 25; // Default: first level cost
        return this.driverProgress.getWaveLuckCost();
    }

    /**
     * Upgrade wave luck
     * @deprecated Use driverProgress.upgradeWaveLuck() instead
     */
    upgradeWaveLuck() {
        if (!this.driverProgress) return false;
        return this.driverProgress.upgradeWaveLuck();
    }

    /**
     * Get wave luck bonus (0.0 to 0.50+ with items)
     * This is applied as a multiplier to wave quality: quality *= (1 + bonus)
     * Includes both driver skill bonus and item effects.
     */
    getWaveLuckBonus() {
        // Base bonus from driver skill (0.0 to 0.50)
        let bonus = this.driverProgress ? this.driverProgress.getWaveLuckBonus() : 0;

        // Add item effects (waveLuck items provide additive bonus)
        if (this.statCalculator) {
            const itemEffects = this.statCalculator.calculateActiveItemEffects().waveLuck;
            if (itemEffects) {
                bonus += itemEffects.add;
            }
            // Add collection milestone bonus (waveLuck is additive percentage)
            bonus += this.statCalculator.getCollectionBonus('waveLuck');
        }

        return bonus;
    }

    /**
     * Get wave luck as a display multiplier (1.00x to 1.50x+ with items)
     * Includes both driver skill bonus and item effects.
     */
    getWaveLuckMultiplier() {
        return 1 + this.getWaveLuckBonus();
    }

    /**
     * Get restored momentum from saved session (always restored, regardless of time away)
     */
    getRestoredMomentum() {
        return this.state.session?.exitMomentum || 0;
    }

    /**
     * Save session state for offline simulation
     * Called on beforeunload and periodically during gameplay
     */
    saveSessionState(currentMomentum = 0) {
        if (!this.state.session) {
            this.state.session = {};
        }
        this.state.session.exitTime = Date.now();
        this.state.session.exitMomentum = currentMomentum;
        this.state.session.exitTotalMiles = this.state.totalMiles;
        this.state.session.exitGameTime = this.state.gameTime;
        this.save(true); // Force immediate save
    }

    /**
     * Calculate what happened while offline
     * Returns { offlineSeconds, simulatedSeconds, milesDriven, wavesFound, restoredMomentum }
     */
    calculateOfflineProgress() {
        // Check for OfflineSimulator state - if present, simulator handles progress
        // and this fallback calculation should be skipped
        const slotKey = this.saveManager?.getActiveSaveKey() || 'roadTripSave_anonymous';
        if (localStorage.getItem(`offlineSimulatorState_${slotKey}`)) {
            console.log('[GameState] Skipping calculateOfflineProgress - OfflineSimulator state exists for slot:', slotKey);
            return null;
        }

        const session = this.state.session;

        if (!session?.exitTime) {
            return null; // No session data, first load
        }

        const now = Date.now();
        const offlineMs = now - session.exitTime;
        const offlineSeconds = offlineMs / 1000;

        // Cap by available fuel (not tank capacity)
        const availableFuel = this.getFuel();
        const simulatedSeconds = Math.min(offlineSeconds, availableFuel);

        // Skip if offline for less than 1 minute (avoid showing modal for quick refreshes)
        if (simulatedSeconds < 60) {
            return null;
        }

        // Get idle speed and momentum parameters
        const idleSpeed = this.getIdleSpeed(); // MPH
        const exitMomentum = session.exitMomentum || 0;
        const momentumCap = this.getCurrentMomentumCap();
        const momentumRate = this.getCurrentMomentumRate(); // MPH per second

        // Apply time compression to simulated time
        const timeScale = this.getEffectiveTimeScale();
        const gameSeconds = simulatedSeconds * timeScale;

        // Simulate momentum growth over time
        // Phase 1: Momentum grows from exitMomentum to cap
        // Phase 2: Momentum stays at cap for remaining time
        const momentumToGain = Math.max(0, momentumCap - exitMomentum);
        const timeToReachCap = momentumRate > 0 ? momentumToGain / momentumRate : Infinity;

        let milesDriven;
        let restoredMomentum;
        const cappedByFuel = offlineSeconds > availableFuel;

        if (cappedByFuel) {
            // Ran out of fuel - momentum resets to 0 when rejoining
            restoredMomentum = 0;
        } else if (gameSeconds <= timeToReachCap) {
            // Still growing - calculate current momentum
            restoredMomentum = exitMomentum + (momentumRate * gameSeconds);
        } else {
            // Reached cap during simulation
            restoredMomentum = momentumCap;
        }

        if (gameSeconds <= timeToReachCap) {
            // Never reaches cap - momentum grows linearly the whole time
            // Distance = integral of (idleSpeed + exitMomentum + rate*t) dt from 0 to T
            // = idleSpeed*T + exitMomentum*T + 0.5*rate*T^2
            const T = gameSeconds;
            const distanceSeconds = idleSpeed * T + exitMomentum * T + 0.5 * momentumRate * T * T;
            milesDriven = distanceSeconds / 3600 * MAIN_GAME_MILES_FACTOR; // Match active play rate
        } else {
            // Reaches cap, then continues at capped speed
            // Phase 1: growing momentum (0 to timeToReachCap)
            const T1 = timeToReachCap;
            const phase1Distance = idleSpeed * T1 + exitMomentum * T1 + 0.5 * momentumRate * T1 * T1;

            // Phase 2: constant speed at cap (timeToReachCap to gameSeconds)
            const T2 = gameSeconds - timeToReachCap;
            const cappedSpeed = idleSpeed + momentumCap;
            const phase2Distance = cappedSpeed * T2;

            milesDriven = (phase1Distance + phase2Distance) / 3600 * MAIN_GAME_MILES_FACTOR;
        }

        // Check for wave spawns in that mile range
        const startMiles = session.exitTotalMiles;
        const endMiles = startMiles + milesDriven;
        const wavesFound = this.waveState.checkOfflineWaveSpawns(startMiles, endMiles);

        // Advance game time
        const newGameTime = (session.exitGameTime + gameSeconds) % this.GAME_DAY_LENGTH;

        return {
            offlineSeconds,
            simulatedSeconds,
            cappedByFuel,
            milesDriven,
            wavesFound,
            restoredMomentum,
            newGameTime,
            timeScale
        };
    }

    /**
     * Apply offline progress to game state
     */
    applyOfflineProgress(progress) {
        if (!progress) return;

        // Add miles
        this.addTotalMiles(progress.milesDriven);

        // Update game time
        this.state.gameTime = progress.newGameTime;

        // Store restored momentum so it can be applied when game resumes
        // This is read by getRestoredMomentum() in startFadeInAndMusic()
        if (progress.restoredMomentum !== undefined) {
            this.state.session.exitMomentum = progress.restoredMomentum;
        }

        // Clear exit time (will be set fresh on next exit)
        this.state.session.exitTime = null;

        this.save();
    }

    save(force = false) {
        // Ensure lifetimeMiles is never less than totalMiles before saving
        if (this.state.lifetimeMiles < this.state.totalMiles) {
            this.state.lifetimeMiles = this.state.totalMiles;
        }
        this.saveManager.save(this.state, force);
    }

    flushPendingSave() {
        this.saveManager.flushPendingSave(this.state);
    }

    // Get effective timeScale including item effects
    // timeScale=1 means real time, higher values compress time
    getEffectiveTimeScale() {
        let timeScale = this.BASE_TIME_SCALE;

        // Apply item effects (multiplicative)
        if (this.statCalculator) {
            timeScale = this.statCalculator.applyItemEffects('timeScale', timeScale);
        }

        return timeScale;
    }

    update(deltaTime) {
        // Update game clock using unified timeScale
        const timeScale = this.getEffectiveTimeScale();
        this.state.gameTime += deltaTime * timeScale;

        // Wrap around at 24 hours
        if (this.state.gameTime >= this.GAME_DAY_LENGTH) {
            this.state.gameTime -= this.GAME_DAY_LENGTH;
        }

        // Track time since game start and last momentum save
        this._gameUptime = (this._gameUptime || 0) + deltaTime;
        this._momentumSaveTimer = (this._momentumSaveTimer || 0) + deltaTime;
    }

    /**
     * Periodically save momentum to session state (call from game loop)
     * Saves every 2 seconds to ensure momentum is preserved on unexpected exits
     */
    updateMomentum(currentMomentum) {
        // Wait 1 second after game start before saving (brief stabilization period)
        if (this._gameUptime < 1) return;

        // Only save every 2 seconds to avoid excessive writes
        if (this._momentumSaveTimer < 2) return;
        this._momentumSaveTimer = 0;

        if (!this.state.session) {
            this.state.session = {};
        }

        // Always save current state (momentum and session data)
        this.state.session.exitMomentum = currentMomentum;
        this.state.session.exitTime = Date.now();
        this.state.session.exitTotalMiles = this.state.totalMiles;
        this.state.session.exitGameTime = this.state.gameTime;
        this.save();
    }

    getGameTimeOfDay() {
        // Returns object with hours, minutes, seconds
        const totalSeconds = Math.floor(this.state.gameTime);
        const hours = Math.floor(totalSeconds / 3600) % 24;
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return { hours, minutes, seconds };
    }

    getGameTimeString() {
        // Returns formatted time string like "6:42 PM"
        const time = this.getGameTimeOfDay();
        const period = time.hours >= 12 ? 'PM' : 'AM';
        const displayHours = time.hours % 12 || 12;
        const minutesStr = time.minutes.toString().padStart(2, '0');

        return `${displayHours}:${minutesStr} ${period}`;
    }

    getGameTimeNormalized() {
        // Returns 0.0-1.0 representing position in 24-hour cycle
        // 0.0 = midnight, 0.25 = 6 AM, 0.5 = noon, 0.75 = 6 PM
        return this.state.gameTime / this.GAME_DAY_LENGTH;
    }

    getCurrentCar() {
        return this.state.cars[this.state.currentCarId];
    }

    getCurrentBaseSpeed() {
        const car = this.getCurrentCar();
        const carConfig = this.carsConfig[this.state.currentCarId];
        const level = car.upgradeLevels.baseSpeed || 0;
        const increment = (carConfig.maxBaseSpeed - carConfig.baseSpeed) / 10; // 10 levels
        const baseValue = carConfig.baseSpeed + (level * increment);
        // Apply tier stat multiplier, then add Ascension Bonus
        const tieredValue = baseValue * this.getTierStatMultiplier();
        const ab = car.ascensionBonus?.baseSpeed || 0;
        const withAB = tieredValue + ab;
        // Apply prestige skill bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.skillBonuses?.baseSpeed || 0;
        return withAB * (1 + prestigeBonus);
    }

    /**
     * Get idle speed including item effects AND collection bonuses (used for offline simulation)
     * This is the speed the car travels at without any clicks (base speed + all bonuses)
     */
    getIdleSpeed() {
        let idleSpeed = this.getCurrentBaseSpeed();

        // Apply item effects AND collection bonuses if stat calculator is available
        if (this.statCalculator) {
            idleSpeed = this.statCalculator.applyAllBonuses('baseSpeed', idleSpeed);
        }

        return idleSpeed;
    }

    getCurrentAcceleration() {
        const car = this.getCurrentCar();
        const carConfig = this.carsConfig[this.state.currentCarId];
        const level = car.upgradeLevels.acceleration || 0;
        const increment = (carConfig.maxAcceleration - carConfig.baseAcceleration) / 10;
        const baseValue = carConfig.baseAcceleration + (level * increment);
        // Apply tier stat multiplier, then add Ascension Bonus
        const tieredValue = baseValue * this.getTierStatMultiplier();
        const ab = car.ascensionBonus?.acceleration || 0;
        const withAB = tieredValue + ab;
        // Apply prestige skill bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.skillBonuses?.acceleration || 0;
        return withAB * (1 + prestigeBonus);
    }

    getCurrentMaxSpeed() {
        const car = this.getCurrentCar();
        const carConfig = this.carsConfig[this.state.currentCarId];
        const level = car.upgradeLevels.maxSpeed || 0;
        // Max speed upgrades: each level adds a portion toward doubling the base
        const increment = carConfig.maxSpeed / 10; // Simple linear increase
        const baseValue = carConfig.maxSpeed + (level * increment);
        // Apply tier stat multiplier, then add Ascension Bonus
        const tieredValue = baseValue * this.getTierStatMultiplier();
        const ab = car.ascensionBonus?.maxSpeed || 0;
        const withAB = tieredValue + ab;
        // Apply prestige skill bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.skillBonuses?.maxSpeed || 0;
        return withAB * (1 + prestigeBonus);
    }

    getCurrentAerodynamics() {
        const carConfig = this.carsConfig[this.state.currentCarId];
        // Aerodynamics is a drag coefficient - lower is better, so don't apply tier bonus
        return carConfig.aerodynamics;
    }

    getCurrentCruiseDuration() {
        const car = this.getCurrentCar();
        const carConfig = this.carsConfig[this.state.currentCarId];
        const level = car.upgradeLevels.cruiseDuration || 0;
        const increment = (carConfig.maxCruiseDuration - carConfig.baseCruiseDuration) / 10;
        const baseValue = carConfig.baseCruiseDuration + (level * increment);
        // Add Ascension Bonus (no tier multiplier for time-based stats)
        const ab = car.ascensionBonus?.cruiseDuration || 0;
        const withAB = baseValue + ab;
        // Apply prestige skill bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.skillBonuses?.cruiseDuration || 0;
        return withAB * (1 + prestigeBonus);
    }

    getCurrentCruiseCooldown() {
        const car = this.getCurrentCar();
        const carConfig = this.carsConfig[this.state.currentCarId];
        const level = car.upgradeLevels.cruiseCooldown || 0;
        // Cooldown decreases with upgrades
        const decrement = (carConfig.baseCruiseCooldown - 10) / 10; // Min 10 seconds
        const baseValue = carConfig.baseCruiseCooldown - (level * decrement);
        // Note: cruiseCooldown does NOT receive AB to keep values predictable through upgrade chain
        // Apply prestige skill bonus (multiplicative - reduces cooldown)
        const prestigeBonus = this.state.prestige?.skillBonuses?.cruiseCooldown || 0;
        // For cooldown, bonus reduces the time, so we multiply by (1 - bonus)
        const withPrestige = baseValue * (1 - prestigeBonus);
        // Clamp to minimum 5 seconds (as specified)
        return Math.max(5, withPrestige);
    }

    getCurrentMomentumCap() {
        const car = this.getCurrentCar();
        const carConfig = this.carsConfig[this.state.currentCarId];
        const level = car.upgradeLevels.momentum || 0;
        const increment = (carConfig.maxMomentumCap - carConfig.baseMomentumCap) / 10;
        const baseValue = carConfig.baseMomentumCap + (level * increment);
        // Apply tier multiplier (momentum is a speed-based stat)
        const tieredValue = baseValue * this.getTierStatMultiplier();
        // Add Ascension Bonus
        const ab = car.ascensionBonus?.momentum || 0;
        const withAB = tieredValue + ab;
        // Apply prestige skill bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.skillBonuses?.momentum || 0;
        return withAB * (1 + prestigeBonus);
    }

    getCurrentMomentumRate() {
        const car = this.getCurrentCar();
        const carConfig = this.carsConfig[this.state.currentCarId];
        const level = car.upgradeLevels.momentumRate || 0;
        const baseRate = carConfig.baseMomentumRate || 0.05; // Fallback for old saves
        const maxRate = carConfig.maxMomentumRate || 0.10;
        const increment = (maxRate - baseRate) / 10;
        const baseValue = baseRate + (level * increment);
        // Apply tier multiplier (momentum rate is a speed-based stat)
        const tieredValue = baseValue * this.getTierStatMultiplier();
        // Add Ascension Bonus
        const ab = car.ascensionBonus?.momentumRate || 0;
        const withAB = tieredValue + ab;
        // Apply prestige skill bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.skillBonuses?.momentumRate || 0;
        return withAB * (1 + prestigeBonus);
    }

    /**
     * Get Ascension Bonus for a specific stat on the current car
     * @param {string} statType - The stat type (baseSpeed, acceleration, etc.)
     * @returns {number} The AB value (0 if none)
     */
    getAscensionBonus(statType) {
        const car = this.getCurrentCar();
        return car.ascensionBonus?.[statType] || 0;
    }

    /**
     * Get all Ascension Bonuses for the current car
     * @returns {object} Object with all AB values
     */
    getAllAscensionBonuses() {
        const car = this.getCurrentCar();
        return car.ascensionBonus || {};
    }

    // ===== Car Tier System Methods =====

    /**
     * Get the current car's tier based on miles invested
     * @param {string} carId - Optional car ID (defaults to current car)
     * @returns {string} The tier name (faded, standard, pristine, golden, vectorized)
     */
    getCarTier(carId = null) {
        const id = carId || this.state.currentCarId;
        const car = this.state.cars[id];
        if (!car) return 'faded';

        const milesInvested = car.milesInvested || 0;
        return CarTierSystem.calculateTier(milesInvested, id);
    }

    /**
     * Get progress info toward the next tier
     * @param {string} carId - Optional car ID (defaults to current car)
     * @returns {object} Progress info including current tier, next tier, progress percentage
     */
    getCarTierProgress(carId = null) {
        const id = carId || this.state.currentCarId;
        const car = this.state.cars[id];
        if (!car) {
            return {
                progress: 0,
                currentTier: 'faded',
                nextTier: 'standard',
                milesNeeded: CarTierSystem.BASE_TIER_THRESHOLDS[1]
            };
        }

        const milesInvested = car.milesInvested || 0;
        return CarTierSystem.getTierProgress(milesInvested, id);
    }

    /**
     * Get the upgrade level cap for the current car's tier
     * @param {string} carId - Optional car ID (defaults to current car)
     * @returns {number} The max upgrade level allowed by the tier
     */
    getTierUpgradeCap(carId = null) {
        const tier = this.getCarTier(carId);
        return CarTierSystem.getTierUpgradeCap(tier);
    }

    /**
     * Get the stat multiplier for the current car's tier
     * @param {string} carId - Optional car ID (defaults to current car)
     * @returns {number} The stat multiplier (1.0 to 1.3)
     */
    getTierStatMultiplier(carId = null) {
        const tier = this.getCarTier(carId);
        return CarTierSystem.getTierStatMultiplier(tier);
    }

    /**
     * Get the tier color for the current car
     * @param {string} carId - Optional car ID (defaults to current car)
     * @returns {string} The hex color code
     */
    getTierColor(carId = null) {
        const tier = this.getCarTier(carId);
        return CarTierSystem.getTierColor(tier);
    }

    /**
     * Get the tier color by tier name
     * @param {string} tierName - The tier name (e.g., 'golden', 'vectorized')
     * @returns {string} The hex color code
     */
    getTierColorByName(tierName) {
        return CarTierSystem.getTierColor(tierName);
    }

    /**
     * Get miles invested in a car
     * @param {string} carId - Optional car ID (defaults to current car)
     * @returns {number} Total miles invested in upgrades
     */
    getMilesInvested(carId = null) {
        const id = carId || this.state.currentCarId;
        const car = this.state.cars[id];
        return car ? (car.milesInvested || 0) : 0;
    }

    /**
     * Calculate what would happen if purchasing a new car (ascension preview)
     * @param {string} newCarId - The car to potentially purchase
     * @returns {object} Ascension preview data
     */
    calculateAscension(newCarId) {
        const oldCar = this.getCurrentCar();
        const oldCarId = this.state.currentCarId;
        const oldCarConfig = this.carsConfig[oldCarId];
        return CarTierSystem.calculateAscension(oldCar, oldCarId, newCarId, oldCarConfig);
    }

    updateMiles(travelDistance) {
        // Calculate distance traveled since last update
        // travelDistance is already scaled by timeScale (applied to visual speed in main.js)
        const distanceTraveled = travelDistance - this.distanceAtLastMileUpdate;

        // Convert game units to miles
        // At 60 MPH with timeScale=1: 60 miles/hour (honest, accurate)
        // At 60 MPH with timeScale=4.545: 272 miles/hour (time compressed)
        let milesTraveled = distanceTraveled / this.gameUnitsPerMile;

        // Apply prestige mile earning bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.mileEarningBonus || 0;
        milesTraveled *= (1 + prestigeBonus);

        // Apply collection milestone mileMultiplier bonus (additive to multiplier)
        if (this.statCalculator) {
            const mileMultiplierBonus = this.statCalculator.getCollectionBonus('mileMultiplier');
            milesTraveled *= (1 + mileMultiplierBonus);
        }

        if (milesTraveled >= 0.01) { // Update every 0.01 miles to avoid tiny increments
            this.state.miles += milesTraveled;
            this.state.totalMiles += milesTraveled;
            this.state.lifetimeMiles = (this.state.lifetimeMiles || 0) + milesTraveled; // True lifetime (never resets)
            this.distanceAtLastMileUpdate = travelDistance;
            this.save();
        }
    }

    getMiles() {
        return this.state.miles;
    }

    getTotalMiles() {
        return this.state.totalMiles || 0; // Return 0 if undefined (for backward compatibility)
    }

    /**
     * Add miles directly (used for offline progress)
     * @param {number} miles - Miles to add (already in mile units, not game units)
     */
    addTotalMiles(miles) {
        if (miles <= 0) return;

        // Apply prestige mile earning bonus (multiplicative)
        const prestigeBonus = this.state.prestige?.mileEarningBonus || 0;
        let adjustedMiles = miles * (1 + prestigeBonus);

        // Apply collection milestone mileMultiplier bonus (additive to multiplier)
        if (this.statCalculator) {
            const mileMultiplierBonus = this.statCalculator.getCollectionBonus('mileMultiplier');
            adjustedMiles *= (1 + mileMultiplierBonus);
        }

        this.state.miles += adjustedMiles;
        this.state.totalMiles += adjustedMiles;
        this.state.lifetimeMiles = (this.state.lifetimeMiles || 0) + adjustedMiles; // True lifetime (never resets)
        this.save();
    }

    /**
     * Get lifetime miles (never resets, used for leaderboards)
     */
    getLifetimeMiles() {
        return this.state.lifetimeMiles || 0;
    }

    // =========================================================================
    // LEADERBOARD PERIOD TRACKING (weekly/daily miles)
    // =========================================================================

    /**
     * Get the start of the current week (Monday 00:00 UTC)
     */
    _getWeekStartUTC() {
        const now = new Date();
        const dayOfWeek = now.getUTCDay(); // 0 = Sunday, 1 = Monday, etc.
        const daysSinceMonday = (dayOfWeek + 6) % 7; // Convert to Monday = 0
        const monday = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate() - daysSinceMonday,
            0, 0, 0, 0
        ));
        return monday.getTime();
    }

    /**
     * Get the start of the current day (00:00 UTC)
     */
    _getDayStartUTC() {
        const now = new Date();
        const dayStart = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate(),
            0, 0, 0, 0
        ));
        return dayStart.getTime();
    }

    /**
     * Check and reset period tracking if we've crossed into a new week/day
     * Call this on game load and before syncing to cloud
     */
    checkPeriodResets() {
        if (!this.state.leaderboardPeriods) {
            this.state.leaderboardPeriods = {
                weekStartTime: null,
                weekStartMiles: 0,
                dayStartTime: null,
                dayStartMiles: 0
            };
        }

        const periods = this.state.leaderboardPeriods;
        const currentWeekStart = this._getWeekStartUTC();
        const currentDayStart = this._getDayStartUTC();
        let changed = false;

        // Check weekly reset
        if (periods.weekStartTime !== currentWeekStart) {
            console.log('[GameState] New week detected, resetting weekly miles tracking');
            periods.weekStartTime = currentWeekStart;
            periods.weekStartMiles = this.state.totalMiles || 0;
            changed = true;
        }

        // Check daily reset
        if (periods.dayStartTime !== currentDayStart) {
            console.log('[GameState] New day detected, resetting daily miles tracking');
            periods.dayStartTime = currentDayStart;
            periods.dayStartMiles = this.state.totalMiles || 0;
            changed = true;
        }

        if (changed) {
            this.save();
        }

        return changed;
    }

    /**
     * Get miles earned this week (since Monday 00:00 UTC)
     */
    getWeeklyMiles() {
        this.checkPeriodResets();
        const weekStartMiles = this.state.leaderboardPeriods?.weekStartMiles || 0;
        return Math.max(0, (this.state.totalMiles || 0) - weekStartMiles);
    }

    /**
     * Get miles earned today (since 00:00 UTC)
     */
    getDailyMiles() {
        this.checkPeriodResets();
        const dayStartMiles = this.state.leaderboardPeriods?.dayStartMiles || 0;
        return Math.max(0, (this.state.totalMiles || 0) - dayStartMiles);
    }

    // Car upgrade cost multipliers (relative to starter car base costs)
    static CAR_UPGRADE_COST_MULTIPLIERS = {
        starter: 1,
        sedan: 2,
        sports: 3,
        supercar: 4.5,
        hypercar: 6
    };

    // Prestige scaling: +25% per prestige level
    static PRESTIGE_UPGRADE_COST_MULTIPLIER = 0.25;

    // Upgrade purchase methods
    getUpgradeCost(upgradeType) {
        const car = this.getCurrentCar();
        const currentLevel = car.upgradeLevels[upgradeType] || 0;
        const upgrade = this.upgrades[upgradeType];

        // If already at max level, return null (no cost for non-existent next level)
        if (upgrade && currentLevel >= upgrade.maxLevel) {
            return null;
        }

        let baseCost = null;

        // Get base costs (starter car costs used as reference, multiplied for other cars)
        if (upgrade && upgrade.baseCosts) {
            if (upgrade.baseCosts[currentLevel] !== undefined) {
                baseCost = upgrade.baseCosts[currentLevel];
            }
        }

        // Fall back to old cost structure if available
        if (baseCost === null && upgrade && upgrade.costs && upgrade.costs[currentLevel] !== undefined) {
            baseCost = upgrade.costs[currentLevel];
        }

        // Final fallback to formula
        if (baseCost === null && upgrade && upgrade.baseCost) {
            baseCost = Math.floor(upgrade.baseCost * Math.pow(upgrade.costMultiplier || 1.5, currentLevel));
        }

        if (baseCost === null) {
            console.error(`No cost found for upgrade ${upgradeType} at level ${currentLevel}`);
            return 999999; // Return high number if no cost found
        }

        // Apply car-specific multiplier
        const carMultiplier = GameState.CAR_UPGRADE_COST_MULTIPLIERS[this.state.currentCarId] || 1;

        // Apply prestige scaling (+25% per prestige level)
        const prestigeLevel = this.state.prestige?.level || 0;
        const prestigeMultiplier = 1 + (prestigeLevel * GameState.PRESTIGE_UPGRADE_COST_MULTIPLIER);

        return Math.floor(baseCost * carMultiplier * prestigeMultiplier);
    }

    canAffordUpgrade(upgradeType) {
        const cost = this.getUpgradeCost(upgradeType);
        // null cost means max level - can't afford something that doesn't exist
        if (cost === null) return false;
        return this.state.miles >= cost;
    }

    purchaseUpgrade(upgradeType) {
        if (!this.canAffordUpgrade(upgradeType)) {
            console.warn('Cannot afford upgrade:', upgradeType);
            return false;
        }

        const car = this.getCurrentCar();
        const upgrade = this.upgrades[upgradeType];

        if (!upgrade) {
            console.error('Upgrade type not found in upgrades data:', upgradeType);
            return false;
        }

        const currentLevel = car.upgradeLevels[upgradeType] || 0;
        const maxLevel = CarTierSystem.MAX_UPGRADE_LEVEL;

        if (currentLevel >= maxLevel) {
            console.warn('Upgrade at max level:', upgradeType, currentLevel, upgrade.maxLevel);
            return false;
        }

        const cost = this.getUpgradeCost(upgradeType);

        // Safety check: prevent negative miles
        if (this.state.miles < cost) {
            console.error('SAFETY CHECK FAILED: Insufficient miles for upgrade');
            console.error(`Has: ${this.state.miles}, Needs: ${cost}`);
            return false;
        }

        this.state.miles -= cost;
        console.log(`[GameState] Spent ${cost} miles on ${upgradeType}`);
        car.upgradeLevels[upgradeType] = currentLevel + 1;

        // Track miles invested for tier progression
        if (car.milesInvested === undefined) {
            car.milesInvested = 0;
        }
        car.milesInvested += cost;

        this.save(true); // Force immediate save for purchases

        // Invalidate stat calculator cache for this upgrade type
        if (this.statCalculator) {
            this.statCalculator.invalidateStat(upgradeType);
        }

        return true;
    }

    // Car management methods
    getNextCar() {
        const currentIndex = this.carOrder.indexOf(this.state.currentCarId);
        if (currentIndex === -1 || currentIndex >= this.carOrder.length - 1) {
            return null; // No next car (already at max)
        }
        const nextCarId = this.carOrder[currentIndex + 1];
        return {
            id: nextCarId,
            config: this.carsConfig[nextCarId] // Get from config, not state
        };
    }

    /**
     * Get the unlock cost for a car, scaled by prestige level
     * @param {string} carId - The car to get unlock cost for
     * @returns {number} The scaled unlock cost
     */
    getCarUnlockCost(carId) {
        const car = this.carsConfig[carId];
        if (!car) return 0;

        const baseCost = car.unlockCost;

        // Apply prestige scaling (+25% per prestige level, same as upgrades)
        const prestigeLevel = this.state.prestige?.level || 0;
        const prestigeMultiplier = 1 + (prestigeLevel * GameState.PRESTIGE_UPGRADE_COST_MULTIPLIER);

        return Math.floor(baseCost * prestigeMultiplier);
    }

    canAffordCar(carId) {
        const cost = this.getCarUnlockCost(carId);
        return this.state.miles >= cost;
    }

    purchaseCar(carId) {
        if (!this.canAffordCar(carId)) {
            console.warn('Cannot afford car:', carId);
            return false;
        }

        // Check if car already exists in state
        if (this.state.cars[carId]) {
            if (this.state.cars[carId].unlocked) {
                console.warn('Car already unlocked:', carId);
                return false;
            }
        } else {
            // Add car to state from config (deep copy)
            this.state.cars[carId] = JSON.parse(JSON.stringify(this.carsConfig[carId]));
        }

        const newCar = this.state.cars[carId];
        const scaledCost = this.getCarUnlockCost(carId); // Get prestige-scaled cost

        // Safety check: verify we can afford it (prevents negative miles from edge cases)
        if (this.state.miles < scaledCost) {
            console.error('SAFETY CHECK FAILED: Insufficient miles for car purchase');
            console.error(`Has: ${this.state.miles}, Needs: ${scaledCost}`);
            return false;
        }

        // Calculate ascension transfer from old car
        const ascension = this.calculateAscension(carId);

        // Deduct prestige-scaled cost
        this.state.miles -= scaledCost;
        console.log(`[GameState] Spent ${scaledCost} miles on ${carId}`);

        // Update the saved car's unlock cost to match current config (cleanup old data)
        newCar.unlockCost = this.carsConfig[carId].unlockCost;

        // Apply ascension transfer to new car
        // If old car had no upgrades, don't transfer miles (new car starts at Faded)
        const oldCar = this.getCurrentCar();
        const hadUpgrades = oldCar.upgradeLevels &&
            Object.values(oldCar.upgradeLevels).some(level => level > 0);
        newCar.milesInvested = hadUpgrades ? ascension.transfer.milesTransferred : 0;

        // Store Ascension Bonus (AB) on new car - replaces level transfers
        // AB does NOT persist across multiple ascensions, so we overwrite any existing AB
        newCar.ascensionBonus = { ...ascension.ascensionBonus };

        // Reset upgrade levels to 0 - player starts fresh but with AB bonus
        newCar.upgradeLevels = {
            baseSpeed: 0,
            acceleration: 0,
            maxSpeed: 0,
            cruiseDuration: 0,
            cruiseCooldown: 0,
            momentum: 0
        };

        console.log(`Ascension complete:`, ascension);
        console.log(`New car ${carId} starts at tier ${ascension.newCar.tier} with ${newCar.milesInvested} miles invested`);
        console.log(`Ascension Bonus applied:`, newCar.ascensionBonus);

        // Unlock the car
        newCar.unlocked = true;

        // Switch to the new car
        this.state.currentCarId = carId;

        this.save(true); // Force immediate save for car purchases
        return ascension; // Return ascension data for UI feedback
    }

    getCruiseControlClicks() {
        const car = this.getCurrentCar();
        const baseClicks = car.baseCruiseControlClicks || 0;
        const upgradeLevel = car.upgradeLevels.cruiseClicks || 0;
        const upgradeEffect = this.upgrades.cruiseClicks.effect;
        return baseClicks + (upgradeLevel * upgradeEffect);
    }

    getCruiseControlCooldown() {
        const car = this.getCurrentCar();
        const baseCooldown = car.baseCruiseControlCooldown || 60;
        const upgradeLevel = car.upgradeLevels.cruiseCooldown || 0;
        const upgradeEffect = this.upgrades.cruiseCooldown.effect;
        // Ensure cooldown doesn't go below 10 seconds
        return Math.max(10, baseCooldown + (upgradeLevel * upgradeEffect));
    }

    // ===== Waves Methods (delegated to WaveStateManager) =====

    addAvailablePack(packData) {
        return this.waveState.addAvailablePack(packData);
    }

    removeAvailablePack(milestone) {
        return this.waveState.removeAvailablePack(milestone);
    }

    getAvailablePacks() {
        return this.waveState.getAvailablePacks();
    }

    /**
     * @deprecated Use getUIState().hasSeenWave(milestone) instead
     */
    hasSeenWave(milestone) {
        return this.uiState.hasSeenWave(milestone);
    }

    /**
     * @deprecated Use getUIState().getUnseenWaveCount() instead
     */
    getUnseenWaveCount() {
        return this.uiState.getUnseenWaveCount();
    }

    /**
     * @deprecated Use getUIState().markAllWavesSeen() instead
     */
    markAllWavesSeen() {
        return this.uiState.markAllWavesSeen();
    }

    /**
     * @deprecated Use getUIState().cleanupSeenWaves() instead
     */
    cleanupSeenWaves() {
        return this.uiState.cleanupSeenWaves();
    }

    purchasePack(milestone) {
        return this.waveState.purchasePack(milestone);
    }

    openPack(milestone, generatedItems = null) {
        return this.waveState.openPack(milestone, generatedItems);
    }

    confirmPackPurchase(milestone) {
        return this.waveState.confirmPackPurchase(milestone);
    }

    setNextMilestone(milestone) {
        return this.waveState.setNextMilestone(milestone);
    }

    // ===== Inventory Methods =====
    // NOTE: Now working with full item objects instead of IDs

    addItem(itemObject) {
        // Check if item with this ID already exists (shouldn't happen with unique generated IDs)
        const exists = this.state.inventory.items.some(item => item.id === itemObject.id);
        if (!exists) {
            this.state.inventory.items.push(itemObject);

            // Track in collection (persists forever across prestiges)
            if (this.collectionStateManager && itemObject.baseId) {
                this.collectionStateManager.recordItemFound(itemObject.baseId);
                this.collectionStateManager.incrementMastery(itemObject.baseId);
            }

            this.save(true);
            return true;
        }
        return false;
    }

    hasItem(itemId) {
        return this.state.inventory.items.some(item => item.id === itemId);
    }

    getItemById(itemId) {
        return this.state.inventory.items.find(item => item.id === itemId);
    }

    equipItem(itemId, slotType) {
        // Find item object in inventory
        const itemObject = this.getItemById(itemId);
        if (!itemObject) {
            console.warn('Item not in inventory:', itemId);
            return false;
        }

        // Unequip from other slots if already equipped elsewhere
        Object.keys(this.state.inventory.equippedSlots).forEach(slot => {
            const equipped = this.state.inventory.equippedSlots[slot];
            if (equipped && equipped.id === itemId) {
                this.state.inventory.equippedSlots[slot] = null;
            }
        });

        // Equip to new slot (store full object)
        this.state.inventory.equippedSlots[slotType] = itemObject;

        this.save(true);

        // Invalidate stat calculator cache
        if (this.statCalculator) {
            this.statCalculator.invalidateItemEffects();
        }
        return true;
    }

    unequipSlot(slotType) {
        this.state.inventory.equippedSlots[slotType] = null;
        this.save(true);

        // Invalidate stat calculator cache
        if (this.statCalculator) {
            this.statCalculator.invalidateItemEffects();
        }
    }

    discardItem(itemId) {
        // Remove from inventory array
        const index = this.state.inventory.items.findIndex(item => item.id === itemId);
        if (index > -1) {
            this.state.inventory.items.splice(index, 1);
        }

        // Unequip if currently equipped
        Object.keys(this.state.inventory.equippedSlots).forEach(slot => {
            const equipped = this.state.inventory.equippedSlots[slot];
            if (equipped && equipped.id === itemId) {
                this.state.inventory.equippedSlots[slot] = null;
            }
        });

        this.save(true);
        return true;
    }

    // ===== Scrap Wavedust Values =====
    // Scrapping gives ONLY wavedust now, NOT miles. Miles come exclusively from driving.
    static ITEM_SCRAP_WAVEDUST = {
        faded: 3,
        standard: 8,
        pristine: 18,
        golden: 35,
        vectorized: 60
    };

    static WAVE_SCRAP_WAVEDUST = {
        faded: 5,
        standard: 12,
        pristine: 25,
        golden: 45,
        vectorized: 80
    };

    /**
     * Get wave tier name from quality value (for scrap calculations)
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} - Tier name
     */
    _getWaveTier(quality) {
        if (quality >= 0.8) return 'vectorized';
        if (quality >= 0.6) return 'golden';
        if (quality >= 0.4) return 'pristine';
        if (quality >= 0.2) return 'standard';
        return 'faded';
    }

    /**
     * Calculate wavedust scrap value for an item
     * @param {string} itemId - The item ID
     * @returns {number} - Wavedust returned from scrapping
     */
    getItemScrapValue(itemId) {
        const item = this.getItemById(itemId);
        if (!item) return 0;
        return GameState.ITEM_SCRAP_WAVEDUST[item.rarity] || 0;
    }

    /**
     * Calculate wavedust scrap value for an item object directly (for items not yet in inventory)
     * @param {Object} item - The item object with rarity property
     * @returns {number} - Wavedust value for scrapping
     */
    getItemScrapValueDirect(item) {
        if (!item) return 0;
        return GameState.ITEM_SCRAP_WAVEDUST[item.rarity] || 0;
    }

    /**
     * Scrap an item for wavedust
     * @param {string} itemId - The item ID
     * @returns {Object} - { success, base, bonus, total }
     */
    scrapItem(itemId) {
        const baseValue = this.getItemScrapValue(itemId);
        if (baseValue <= 0) return { success: false, base: 0, bonus: 0, total: 0 };

        // Calculate bonus wavedust (scrap bonus still applies to wavedust)
        const multiplier = this.getScrapBonusMultiplier(false);
        const totalValue = Math.floor(baseValue * multiplier);
        const bonusValue = totalValue - baseValue;

        // Add wavedust (NOT miles!)
        this.addWavedust(totalValue);

        // Remove the item
        this.discardItem(itemId);

        return { success: true, base: baseValue, bonus: bonusValue, total: totalValue };
    }

    /**
     * Calculate wavedust scrap value for a wave pack
     * @param {Object} pack - The wave pack object
     * @returns {number} - Wavedust value for scrapping
     */
    getWaveScrapValue(pack) {
        if (!pack) return 0;
        const tier = this._getWaveTier(pack.packQuality || 0);
        return GameState.WAVE_SCRAP_WAVEDUST[tier] || 0;
    }

    /**
     * Scrap a wave pack for wavedust
     * @param {number} milestone - The pack milestone identifier
     * @returns {Object} - { success, base, bonus, total }
     */
    scrapWave(milestone) {
        const packs = this.getAvailablePacks();
        const pack = packs.find(p => p.milestone === milestone);

        if (!pack) return { success: false, base: 0, bonus: 0, total: 0 };

        const baseValue = this.getWaveScrapValue(pack);

        // Calculate bonus wavedust (scrap bonus still applies)
        const multiplier = this.getScrapBonusMultiplier(false);
        const totalValue = Math.floor(baseValue * multiplier);
        const bonusValue = totalValue - baseValue;

        // Add wavedust (NOT miles!)
        this.addWavedust(totalValue);

        // Remove the pack
        this.removeAvailablePack(milestone);

        // Clean up seen tracking
        this.cleanupSeenWaves();

        this.save();
        return { success: true, base: baseValue, bonus: bonusValue, total: totalValue };
    }

    /**
     * Calculate total scrap value of all inventory (for auto-scrap on prestige)
     * NOTE: Uses base values only - no scrap bonus multiplier. This is a safety net, not a farming mechanic.
     * @returns {object} { items: number, waves: number, total: number }
     */
    _calculateInventoryScrapValue() {
        let itemsValue = 0;
        let wavesValue = 0;

        // Calculate value of all inventory items (not equipped)
        const equippedIds = new Set();
        Object.values(this.state.inventory.equippedSlots).forEach(item => {
            if (item) equippedIds.add(item.id);
        });

        for (const item of this.state.inventory.items || []) {
            if (!equippedIds.has(item.id)) {
                const baseValue = GameState.ITEM_SCRAP_WAVEDUST[item.rarity] || 0;
                itemsValue += baseValue;
            }
        }

        // Calculate value of all unopened lots
        const availablePacks = this.state.waves?.availablePacks || [];
        for (const pack of availablePacks) {
            if (!pack.opened) {
                const tier = this._getWaveTier(pack.packQuality || 0);
                wavesValue += GameState.WAVE_SCRAP_WAVEDUST[tier] || 0;
            }
        }

        return {
            items: itemsValue,
            waves: wavesValue,
            total: itemsValue + wavesValue
        };
    }

    getInventoryItems() {
        // Returns array of item objects
        return this.state.inventory.items;
    }

    getEquippedItems() {
        // Returns object mapping slot types to item objects
        return this.state.inventory.equippedSlots;
    }

    /**
     * Get count of unseen items in inventory
     * @deprecated Use getUIState().getUnseenItemCount() instead
     * @returns {number} Number of unseen items
     */
    getUnseenItemCount() {
        return this.uiState.getUnseenItemCount();
    }

    /**
     * Check if a specific item is unseen
     * @deprecated Use !getUIState().hasSeenItem(itemId) instead
     * @param {string} itemId - Item ID to check
     * @returns {boolean} True if item is unseen
     */
    isItemUnseen(itemId) {
        return !this.uiState.hasSeenItem(itemId);
    }

    /**
     * Mark a specific item as seen
     * @deprecated Use getUIState().markItemSeen(itemId) instead
     * @param {string} itemId - Item ID to mark as seen
     */
    markItemSeen(itemId) {
        this.uiState.markItemSeen(itemId);
    }

    /**
     * Mark all current items as seen
     * @deprecated Use getUIState().markAllItemsSeen() instead
     */
    markAllItemsSeen() {
        this.uiState.markAllItemsSeen();
    }

    /**
     * Clean up seen item IDs that are no longer in inventory
     * @deprecated Use getUIState().cleanupSeenItems() instead
     */
    cleanupSeenItems() {
        this.uiState.cleanupSeenItems();
    }

    // ===== Collection Seen State Delegates =====

    /**
     * Get count of unseen collection discoveries
     * @returns {number} Number of unseen discovered items
     */
    getUnseenCollectionCount() {
        return this.uiState.getUnseenCollectionCount();
    }

    /**
     * Check if a collection discovery has been seen
     * @param {string} baseId - Item base ID to check
     * @returns {boolean} True if seen
     */
    hasSeenCollectionItem(baseId) {
        return this.uiState.hasSeenCollectionItem(baseId);
    }

    /**
     * Mark a collection discovery as seen
     * @param {string} baseId - Item base ID to mark as seen
     */
    markCollectionItemSeen(baseId) {
        this.uiState.markCollectionItemSeen(baseId);
    }

    /**
     * Mark all current collection discoveries as seen
     */
    markAllCollectionSeen() {
        this.uiState.markAllCollectionSeen();
    }

    // ===== Item Upgrade Methods (LEGACY - deprecated) =====
    // These methods are kept for legacy item support (items with level/baseEffects)
    // New items use the Quality Fusion system instead

    /**
     * @deprecated Use quality fusion system instead. Kept for legacy items only.
     */
    getItemUpgradeCost(itemId) {
        const item = this.getItemById(itemId);
        if (!item) {
            console.warn('Item not found:', itemId);
            return null;
        }

        // V2 items don't use the level system - use quality fusion instead
        if (!item.isLegacy) {
            return null;
        }

        if (item.level >= MAX_ITEM_LEVEL) {
            return null; // Already at max level
        }

        const costs = ITEM_UPGRADE_COSTS[item.rarity];
        if (!costs) {
            console.warn('No upgrade costs for rarity:', item.rarity);
            return null;
        }

        // Level 1→2 uses index 0, level 9→10 uses index 8
        return costs[item.level - 1];
    }

    /**
     * @deprecated Use quality fusion system instead. Kept for legacy items only.
     */
    canAffordItemUpgrade(itemId) {
        const cost = this.getItemUpgradeCost(itemId);
        if (cost === null) return false;
        return this.state.miles >= cost;
    }

    /**
     * @deprecated Use upgradeItemQuality() instead. Kept for legacy items only.
     */
    upgradeItem(itemId) {
        const item = this.getItemById(itemId);
        if (!item) {
            console.warn('Item not found:', itemId);
            return false;
        }

        // V2 items don't use the level system
        if (!item.isLegacy) {
            console.warn('Cannot level-upgrade v2 items:', itemId);
            return false;
        }

        if (item.level >= MAX_ITEM_LEVEL) {
            console.warn('Item already at max level:', itemId);
            return false;
        }

        const cost = this.getItemUpgradeCost(itemId);
        if (cost === null || this.state.miles < cost) {
            console.warn('Cannot afford item upgrade:', itemId);
            return false;
        }

        // Deduct cost
        this.state.miles -= cost;
        console.log(`[GameState] Spent ${cost} miles on ${item.name}`);

        // Increase level
        item.level += 1;

        // Recalculate effects based on new level (with item upgrade bonus)
        const upgradeBonus = this.getItemUpgradeBonusMultiplier() - 1;
        item.effects = ItemGenerator.computeScaledEffects(item.baseEffects, item.level, upgradeBonus);

        // Also update equipped slot if this item is equipped
        let wasEquipped = false;
        Object.keys(this.state.inventory.equippedSlots).forEach(slot => {
            const equipped = this.state.inventory.equippedSlots[slot];
            if (equipped && equipped.id === itemId) {
                this.state.inventory.equippedSlots[slot] = item;
                wasEquipped = true;
            }
        });

        this.save(true);

        // Invalidate stat calculator cache if item was equipped
        if (wasEquipped && this.statCalculator) {
            this.statCalculator.invalidateItemEffects();
        }

        return true;
    }

    /**
     * @deprecated Use isItemMaxQuality() instead. Kept for legacy items only.
     */
    isItemMaxLevel(itemId) {
        const item = this.getItemById(itemId);
        return item && item.level >= MAX_ITEM_LEVEL;
    }

    // ===== Quality Fusion System =====
    // THE ONLY item upgrade path - fuse 3 items of same rarity + wavedust to upgrade quality

    static RARITY_ORDER = ['faded', 'standard', 'pristine', 'golden', 'vectorized'];

    static QUALITY_UPGRADE_COSTS = {
        faded: { wavedust: 50, materialCount: 3 },      // Faded → Standard
        standard: { wavedust: 120, materialCount: 3 },  // Standard → Pristine
        pristine: { wavedust: 280, materialCount: 3 },  // Pristine → Golden
        golden: { wavedust: 600, materialCount: 3 }     // Golden → Vectorized
        // vectorized: already max, cannot upgrade
    };

    // Rarity multiplier ranges (used to recalculate stats on upgrade)
    static RARITY_MULTIPLIERS = {
        faded: { min: 0.5, max: 0.8 },
        standard: { min: 0.9, max: 1.2 },
        pristine: { min: 1.3, max: 1.7 },
        golden: { min: 1.8, max: 2.3 },
        vectorized: { min: 2.4, max: 3.0 }
    };

    /**
     * Get the next rarity tier
     * @param {string} currentRarity - Current rarity name
     * @returns {string|null} Next rarity or null if at max
     */
    getNextRarity(currentRarity) {
        const index = GameState.RARITY_ORDER.indexOf(currentRarity);
        if (index === -1 || index >= GameState.RARITY_ORDER.length - 1) {
            return null; // Invalid or already at max
        }
        return GameState.RARITY_ORDER[index + 1];
    }

    /**
     * Get quality upgrade cost for an item
     * @param {string} itemId - The item ID
     * @returns {Object|null} { wavedust, materialCount } or null if can't upgrade
     */
    getQualityUpgradeCost(itemId) {
        const item = this.getItemById(itemId);
        if (!item) return null;

        const cost = GameState.QUALITY_UPGRADE_COSTS[item.rarity];
        if (!cost) return null; // Vectorized can't upgrade

        return { ...cost };
    }

    /**
     * Get available material items for quality fusion
     * @param {string} targetItemId - Item being upgraded
     * @returns {Array} Array of item objects that can be used as material
     */
    getAvailablematerial(targetItemId) {
        const targetItem = this.getItemById(targetItemId);
        if (!targetItem) return [];

        // Get equipped item IDs to exclude
        const equippedIds = new Set();
        Object.values(this.state.inventory.equippedSlots).forEach(item => {
            if (item) equippedIds.add(item.id);
        });

        // material must be: same rarity, unequipped, not the target item
        return this.state.inventory.items.filter(item =>
            item.id !== targetItemId &&
            item.rarity === targetItem.rarity &&
            !equippedIds.has(item.id)
        );
    }

    /**
     * Check if player can upgrade an item's quality
     * @param {string} itemId - The item ID
     * @returns {Object} { canUpgrade, reason, cost, availablematerial }
     */
    canUpgradeItemQuality(itemId) {
        const item = this.getItemById(itemId);
        if (!item) {
            return { canUpgrade: false, reason: 'Item not found' };
        }

        const nextRarity = this.getNextRarity(item.rarity);
        if (!nextRarity) {
            return { canUpgrade: false, reason: 'Already at maximum quality' };
        }

        const cost = this.getQualityUpgradeCost(itemId);
        if (!cost) {
            return { canUpgrade: false, reason: 'Cannot upgrade this item' };
        }

        const availablematerial = this.getAvailablematerial(itemId);
        if (availablematerial.length < cost.materialCount) {
            return {
                canUpgrade: false,
                reason: `Need ${cost.materialCount} ${item.rarity} items (have ${availablematerial.length})`,
                cost,
                availablematerial
            };
        }

        if (!this.canAffordWavedust(cost.wavedust)) {
            return {
                canUpgrade: false,
                reason: `Need ${cost.wavedust} wavedust (have ${this.getWavedust()})`,
                cost,
                availablematerial
            };
        }

        return {
            canUpgrade: true,
            cost,
            availablematerial,
            nextRarity
        };
    }

    /**
     * Upgrade an item's quality (fuse material items)
     * @param {string} itemId - The item ID to upgrade
     * @param {string[]} materialItemIds - Array of item IDs to consume
     * @returns {Object|false} Upgraded item or false if failed
     */
    upgradeItemQuality(itemId, materialItemIds) {
        const check = this.canUpgradeItemQuality(itemId);
        if (!check.canUpgrade) {
            console.warn('Cannot upgrade item quality:', check.reason);
            return false;
        }

        const item = this.getItemById(itemId);
        const cost = check.cost;

        // Validate material items
        if (materialItemIds.length !== cost.materialCount) {
            console.warn(`Need exactly ${cost.materialCount} material items`);
            return false;
        }

        // Verify all material items exist and are valid
        const equippedIds = new Set();
        Object.values(this.state.inventory.equippedSlots).forEach(eq => {
            if (eq) equippedIds.add(eq.id);
        });

        for (const materialId of materialItemIds) {
            const material = this.getItemById(materialId);
            if (!material) {
                console.warn('material item not found:', materialId);
                return false;
            }
            if (material.rarity !== item.rarity) {
                console.warn('material must be same rarity as target item');
                return false;
            }
            if (equippedIds.has(materialId)) {
                console.warn('Cannot use equipped item as material');
                return false;
            }
            if (materialId === itemId) {
                console.warn('Cannot use target item as its own material');
                return false;
            }
        }

        // Deduct wavedust
        if (!this.spendWavedust(cost.wavedust)) {
            console.warn('Failed to spend wavedust');
            return false;
        }

        // Remove material items from inventory
        for (const materialId of materialItemIds) {
            this.discardItem(materialId);
        }

        // Upgrade item rarity
        const newRarity = check.nextRarity;
        const oldRarity = item.rarity;
        item.rarity = newRarity;

        // V3+ bespoke items: use fixed stat values from item registry for new tier
        // Apply fusion bonus multiplier to boost the values
        if (item.baseId && itemRegistry.has(item.baseId)) {
            const baseItem = itemRegistry.get(item.baseId);
            if (baseItem.stat_values) {
                const fusionBonus = this.getItemUpgradeBonusMultiplier();

                item.effects = item.effects.map(effect => {
                    const statValues = baseItem.stat_values[effect.type];
                    if (statValues && statValues[newRarity] !== undefined) {
                        const baseValue = statValues[newRarity];
                        let boostedValue;

                        // For multiplicative effects, apply bonus to the bonus portion
                        if (effect.operation === 'multiply') {
                            // e.g., 1.20 with 1.10x bonus = 1 + (0.20 * 1.10) = 1.22
                            boostedValue = 1 + (baseValue - 1) * fusionBonus;
                        } else {
                            // Additive effects: multiply directly
                            boostedValue = baseValue * fusionBonus;
                        }

                        // Round to reasonable precision
                        boostedValue = Math.round(boostedValue * 1000) / 1000;

                        return { ...effect, value: boostedValue };
                    }
                    // Fallback: keep existing value if no stat_values defined
                    return effect;
                });
            }
        }

        // Legacy prestige bonus stats are grandfathered - no changes on fusion
        // (These were added by old prestige system, we just preserve them)

        // V3+ bespoke items: no star inheritance (remove stars property if present)
        delete item.stars;

        // Update item name with new rarity prefix
        // Use baseId to get the true base name from registry (avoids parsing errors)
        let baseName = item.name;
        if (item.baseId && itemRegistry.has(item.baseId)) {
            baseName = itemRegistry.get(item.baseId).name;
        }
        const rarityPrefixes = {
            faded: 'Faded',
            standard: 'Standard',
            pristine: 'Pristine',
            golden: 'Golden',
            vectorized: 'Vectorized'
        };
        const prefix = rarityPrefixes[newRarity];
        item.name = `${prefix} ${baseName}`;

        // Update equipped slot if this item is equipped
        let wasEquipped = false;
        Object.keys(this.state.inventory.equippedSlots).forEach(slot => {
            const equipped = this.state.inventory.equippedSlots[slot];
            if (equipped && equipped.id === itemId) {
                this.state.inventory.equippedSlots[slot] = item;
                wasEquipped = true;
            }
        });

        this.save(true);

        // Invalidate stat calculator cache if item was equipped
        if (wasEquipped && this.statCalculator) {
            this.statCalculator.invalidateItemEffects();
        }

        console.log(`Quality fusion complete: ${oldRarity} → ${newRarity}`);

        // Return item with fusion metadata for celebration modal
        return {
            item,
            oldRarity,
            newRarity
        };
    }

    /**
     * Check if item is at maximum quality (vectorized)
     * @param {string} itemId - The item ID
     * @returns {boolean} True if at max quality
     */
    isItemMaxQuality(itemId) {
        const item = this.getItemById(itemId);
        return item && item.rarity === 'vectorized';
    }

    // ===== Legacy Item Prestige Support =====
    // Item prestige system has been removed, but we keep these methods
    // to support legacy items that may have prestigeBonusStats from old saves

    /**
     * Get all prestige bonus stats for an item (legacy support)
     * Legacy items may still have prestigeBonusStats - display them but don't allow new prestige
     * @param {Object} item - The item object
     * @returns {Array} Array of { type, value } bonus stats
     */
    getItemPrestigeBonusStats(item) {
        // Support both old single stat and new array format
        if (item?.prestigeBonusStats && item.prestigeBonusStats.length > 0) {
            return item.prestigeBonusStats;
        }
        if (item?.prestigeBonusStat) {
            return [item.prestigeBonusStat];
        }
        return [];
    }

    // ===== Prestige Methods =====

    /**
     * Check if prestige is available (player is on final car)
     * @returns {boolean} True if prestige is available
     */
    isPrestigeAvailable() {
        const currentIndex = this.carOrder.indexOf(this.state.currentCarId);
        return currentIndex === this.carOrder.length - 1;
    }

    /**
     * Perform prestige - reset all progress and gain permanent bonuses
     * @param {string} selectedSkill - The skill to apply the bonus to (e.g., 'baseSpeed', 'acceleration')
     * @returns {object|false} Result object with new prestige info, or false if failed
     */
    performPrestige(selectedSkill) {
        if (!this.isPrestigeAvailable()) {
            console.warn('Prestige not available - not on final car');
            return false;
        }

        // Validate selected skill
        if (!selectedSkill || !PrestigeSystem.SELECTABLE_SKILLS[selectedSkill]) {
            console.warn('Invalid skill selected:', selectedSkill);
            return false;
        }

        const currentMiles = this.state.miles;
        const totalMiles = this.state.totalMiles;
        const currentLevel = this.state.prestige?.level || 0;

        // Calculate bonuses
        const bonuses = PrestigeSystem.calculatePrestigeBonus(totalMiles, currentMiles, currentLevel);

        // Update prestige state (bonuses are cumulative)
        const newLevel = currentLevel + 1;
        const newMileBonus = (this.state.prestige?.mileEarningBonus || 0) + bonuses.total;

        // Copy existing skill bonuses and add the new one
        const currentSkillBonuses = this.state.prestige?.skillBonuses || {
            baseSpeed: 0,
            acceleration: 0,
            maxSpeed: 0,
            cruiseDuration: 0,
            cruiseCooldown: 0,
            momentum: 0
        };
        const newSkillBonuses = { ...currentSkillBonuses };
        newSkillBonuses[selectedSkill] = (newSkillBonuses[selectedSkill] || 0) + bonuses.total;

        // Store prestige info before reset
        const prestigeInfo = {
            level: newLevel,
            mileEarningBonus: newMileBonus,
            skillBonuses: newSkillBonuses
        };

        // Preserve values that persist through prestige
        const preservedLifetimeMiles = this.state.lifetimeMiles || 0;
        const preservedDriver = this.state.driver ? { ...this.state.driver } : null;
        const preservedLeaderboardPeriods = this.state.leaderboardPeriods ?
            { ...this.state.leaderboardPeriods } : null;
        const preservedFuel = this.state.fuel ?? 0;
        const preservedAudioSettings = this.state.audioSettings ?
            { ...this.state.audioSettings } : null;
        // Collection persists forever (never wiped on any prestige)
        const preservedCollection = this.state.collection ?
            JSON.parse(JSON.stringify(this.state.collection)) : null;

        // Auto-scrap inventory on prestige (convert to wavedust before wiping)
        const scrapValue = this._calculateInventoryScrapValue();
        if (scrapValue.total > 0) {
            this.addWavedust(scrapValue.total);
            console.log(`[GameState] Auto-scrapped inventory on prestige: ${scrapValue.items} from items, ${scrapValue.waves} from lots = ${scrapValue.total} wavedust`);
        }

        // Now capture wavedust (includes auto-scrap value)
        const preservedWavedust = this.state.wavedust || 0;

        // Reset game state to initial, keeping prestige, driver skills, and lifetime stats
        // Note: Items are ephemeral and get wiped on car prestige (auto-scrapped above)
        this.state = this.createInitialState();
        this.state.prestige = prestigeInfo;

        // IMPORTANT: Invalidate StatCalculator cache - old item effects must be cleared
        if (this.statCalculator) {
            this.statCalculator.invalidateAll();
        }
        this.state.lifetimeMiles = preservedLifetimeMiles;
        this.state.fuel = preservedFuel;
        if (preservedDriver) {
            this.state.driver = preservedDriver;
        }
        if (preservedLeaderboardPeriods) {
            this.state.leaderboardPeriods = preservedLeaderboardPeriods;
        }
        if (preservedAudioSettings) {
            this.state.audioSettings = preservedAudioSettings;
        }
        this.state.wavedust = preservedWavedust;
        // Collection persists forever (never wiped)
        if (preservedCollection) {
            this.state.collection = preservedCollection;
        }
        console.log(`[GameState] Preserved ${preservedWavedust} wavedust through prestige`);
        console.log(`[GameState] Collection preserved: ${this.state.collection?.found?.length || 0} items found`);

        // Reset distance tracking
        this.distanceAtLastMileUpdate = 0;

        // Save immediately
        this.save(true);

        console.log(`Prestige complete! Level ${newLevel}`);
        console.log(`Selected skill: ${selectedSkill} -> +${(bonuses.total * 100).toFixed(2)}%`);
        console.log(`Mile Earning bonus: +${(newMileBonus * 100).toFixed(2)}%`);

        // Emit prestige event for systems to react (e.g., biome reset)
        eventBus.emit(GameEvents.PRESTIGE_PERFORMED, {
            level: newLevel,
            selectedSkill: selectedSkill,
            bonusGained: bonuses.total
        });

        return {
            newLevel,
            selectedSkill,
            skillBonus: newSkillBonuses[selectedSkill],
            totalMileBonus: newMileBonus,
            bonusGained: bonuses.total,
            allSkillBonuses: newSkillBonuses
        };
    }

    /**
     * Get current prestige info
     * @deprecated Use getPrestigeStateManager().getPrestigeInfo() instead
     * @returns {object} Prestige level and bonuses
     */
    getPrestigeInfo() {
        // Delegate to PrestigeStateManager if available, otherwise use inline logic
        if (this.prestigeState) {
            const info = this.prestigeState.getPrestigeInfo();
            // Return simplified format for backward compatibility
            return {
                level: info.level,
                mileEarningBonus: info.mileEarningBonus,
                skillBonuses: info.skillBonuses
            };
        }
        // Fallback for when prestigeState hasn't been initialized yet
        return {
            level: this.state.prestige?.level || 0,
            mileEarningBonus: this.state.prestige?.mileEarningBonus || 0,
            skillBonuses: this.state.prestige?.skillBonuses || {
                baseSpeed: 0,
                acceleration: 0,
                maxSpeed: 0,
                cruiseDuration: 0,
                cruiseCooldown: 0,
                momentum: 0
            }
        };
    }

    /**
     * Get the PrestigeStateManager instance
     * Provides access to prestige state operations through the manager API.
     * @returns {PrestigeStateManager} The prestige state manager
     */
    getPrestigeStateManager() {
        return this.prestigeState;
    }

    /**
     * Get the CollectionStateManager instance
     * Provides access to persistent collection tracking across all prestiges.
     * @returns {CollectionStateManager} The collection state manager
     */
    getCollectionStateManager() {
        return this.collectionStateManager;
    }

    // ===== Audio Settings Methods =====

    getSfxVolume() {
        return this.state.audioSettings?.sfxVolume ?? 100;
    }

    getMusicVolume() {
        return this.state.audioSettings?.musicVolume ?? 50;
    }

    setSfxVolume(volume) {
        if (!this.state.audioSettings) {
            this.state.audioSettings = { sfxVolume: 100, musicVolume: 50 };
        }
        this.state.audioSettings.sfxVolume = Math.max(0, Math.min(100, volume));
        // Don't save on every slider tick - volume is saved with regular game saves
    }

    setMusicVolume(volume) {
        if (!this.state.audioSettings) {
            this.state.audioSettings = { sfxVolume: 100, musicVolume: 50 };
        }
        this.state.audioSettings.musicVolume = Math.max(0, Math.min(100, volume));
        // Don't save on every slider tick - volume is saved with regular game saves
    }

    // Legacy compatibility - muted = volume is 0
    isSfxMuted() {
        return this.getSfxVolume() === 0;
    }

    isMusicMuted() {
        return this.getMusicVolume() === 0;
    }

    setSfxMuted(muted) {
        this.setSfxVolume(muted ? 0 : 100);
    }

    setMusicMuted(muted) {
        this.setMusicVolume(muted ? 0 : 50);
    }
}
